import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BenefitOption, PensionSelectionFormData } from "@/types/pensions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { CalendarIcon, XCircle, AlertTriangle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const selectionSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("enroll"),
    effectiveDate: z.string().min(1, "Effective date is required"),
    benefitLineId: z.string().optional().default(""),
    employerContributionPercent: z
      .number()
      .min(0.03, "Employer contribution must be at least 3%")
      .max(100, "Employer contribution cannot exceed 100%"),
    employeeContributionPercent: z
      .number()
      .min(0.05, "Employee contribution must be at least 5%")
      .max(100, "Employee contribution cannot exceed 100%"),
    isReliefAtSource: z.literal(true),
  }),
  z.object({
    action: z.literal("optOut"),
    effectiveDate: z.string().min(1, "Effective date is required"),
    benefitLineId: z.string().optional().default(""),
    optOutReason: z.string().min(1, "Opt-out reason is required"),
    employerContributionPercent: z.number().optional(),
    employeeContributionPercent: z.number().optional(),
    isReliefAtSource: z.literal(true),
  }),
]);

interface PensionSelectionFormProps {
  eventId: number;
  availableOptions: BenefitOption[];
  payrunValidation: {
    hasApprovedPayruns: boolean;
    earliestChangeDate: string | null;
  };
  currentStatus?: {
    isOptedOut: boolean | undefined;
    benefitLineId?: number | undefined;
    optOutDate?: string | undefined;
    optOutReason?: string;
    currentScheme?: string;
    employerContribution?: number;
    employeeContribution?: number;
  };
  onSubmit: (data: PensionSelectionFormData) => Promise<void>;
  isSubmitting: boolean;
  error?: string | null;
  isEditing?: boolean;
}

const PensionSelectionForm: React.FC<PensionSelectionFormProps> = ({
  eventId,
  availableOptions,
  payrunValidation,
  currentStatus,
  onSubmit,
  isSubmitting,
  error,
  isEditing = false,
}) => {
  // Setup default values with proper initialization of benefitLineId
  const defaultBenefitLineId =
    isEditing &&
    currentStatus &&
    !currentStatus.isOptedOut &&
    currentStatus.benefitLineId
      ? currentStatus.benefitLineId.toString()
      : availableOptions.length > 0
        ? availableOptions[0].id.toString()
        : "";

  const form = useForm<PensionSelectionFormData>({
    resolver: zodResolver(selectionSchema),
    defaultValues: {
      action: currentStatus?.isOptedOut ? "optOut" : "enroll",
      effectiveDate:
        payrunValidation.earliestChangeDate ||
        new Date().toISOString().split("T")[0],
      benefitLineId: defaultBenefitLineId, // Always set a default
      ...(isEditing && currentStatus && !currentStatus.isOptedOut
        ? {
            employerContributionPercent: currentStatus.employerContribution,
            employeeContributionPercent: currentStatus.employeeContribution,
            isReliefAtSource: true,
          }
        : {
            employerContributionPercent: 3,
            employeeContributionPercent: 5,
            isReliefAtSource: true,
          }),
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (isEditing) {
      form.clearErrors();

      // If editing, make sure we set all the right values for the current state
      if (currentStatus) {
        const action = currentStatus.isOptedOut ? "optOut" : "enroll";
        form.setValue("action", action);

        // Ensure benefitLineId is set (it's required for enroll)
        if (!currentStatus.isOptedOut && currentStatus.benefitLineId) {
          form.setValue(
            "benefitLineId",
            currentStatus.benefitLineId.toString(),
          );
          form.setValue(
            "employerContributionPercent",
            currentStatus.employerContribution || 3,
          );
          form.setValue(
            "employeeContributionPercent",
            currentStatus.employeeContribution || 5,
          );
        }

        // Set opt-out reason if available
        if (currentStatus.isOptedOut && currentStatus.optOutReason) {
          form.setValue("optOutReason", currentStatus.optOutReason);
        }
      }

      // Trigger validation of current values
      form.trigger();

      console.log("Form initialized for editing:", form.getValues());
    }
  }, [isEditing, form, currentStatus]);

  // Force validation of initial values after component mounts
  useEffect(() => {
    // Set a small delay to ensure form state is fully initialized
    const timer = setTimeout(() => {
      if (form.getValues("action") === "enroll") {
        // Ensure the form values are properly set and trigger validation
        const employerValue = form.getValues("employerContributionPercent");
        const employeeValue = form.getValues("employeeContributionPercent");
        
        // If values are missing or undefined, set defaults
        if (employerValue === undefined || employerValue < 3) {
          form.setValue("employerContributionPercent", 3);
        }
        
        if (employeeValue === undefined || employeeValue < 5) {
          form.setValue("employeeContributionPercent", 5);
        }
        
        // Force revalidation
        form.trigger();
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [form]);

  // Add effective date field
  const minEffectiveDate = payrunValidation.earliestChangeDate
    ? payrunValidation.earliestChangeDate + "T00:00:00"
    : new Date().toISOString().split("T")[0];

  const action = form.watch("action");
  const selectedBenefitLine = form.watch("benefitLineId");
  const employerContribution = form.watch("employerContributionPercent");
  const employeeContribution = form.watch("employeeContributionPercent");

  const selectedOption = availableOptions.find(
    (opt) => opt.id.toString() === selectedBenefitLine,
  );

  const activeUniqueOptions = availableOptions
    .filter((option) => option.isActive)
    .reduce((acc: BenefitOption[], current) => {
      const x = acc.find((item) => item.internalCode === current.internalCode);
      if (!x) {
        return acc.concat([current]);
      }
      return acc;
    }, []);

  // Create a simpler submit handler that works like the opt-out flow
  const handleFormSubmit = async () => {
    try {
      // Get all form values
      const formValues = form.getValues();
      console.log("Form values on submit:", formValues);
      console.log("Event ID:", eventId);
      console.log("Current status:", currentStatus);
      console.log("isEditing:", isEditing);

      // Force set the benefitLineId from the selected option if empty
      if (formValues.action === "enroll" && selectedBenefitLine) {
        // Set benefitLineId explicitly from the selected value to ensure it's picked up
        formValues.benefitLineId = selectedBenefitLine;
        form.setValue("benefitLineId", selectedBenefitLine, {
          shouldValidate: true,
        });
        console.log("Force set benefitLineId to:", selectedBenefitLine);
      }

      // Check pension scheme explicitly
      if (formValues.action === "enroll") {
        console.log("Checking pension scheme validation:");
        console.log("- Current benefitLineId:", formValues.benefitLineId);
        console.log("- Selected scheme:", selectedOption);
        console.log("- Available options:", availableOptions);

        if (!formValues.benefitLineId) {
          console.error("Missing benefitLineId - forcing default");
          // Force a default value if none is selected and options exist
          if (activeUniqueOptions.length > 0) {
            const defaultOption = activeUniqueOptions[0];
            form.setValue("benefitLineId", defaultOption.id.toString(), {
              shouldValidate: true,
            });
            formValues.benefitLineId = defaultOption.id.toString();
            console.log(
              "Set default benefitLineId:",
              defaultOption.id.toString(),
            );
          }
        }
      }

      // Validate the form before submission
      const isValid = await form.trigger();
      console.log("Form validation result:", isValid);

      if (isValid) {
        console.log("Form is valid, submitting...");
        // Make sure we pass fully validated form data
        // Handle action-specific validation manually
        if (formValues.action === "enroll") {
          if (!formValues.benefitLineId) {
            console.error("Missing benefitLineId");
            form.setError("benefitLineId", {
              type: "manual",
              message: "Please select a pension scheme",
            });
            return;
          }
        } else if (formValues.action === "optOut") {
          if (!formValues.optOutReason) {
            console.error("Missing optOutReason");
            form.setError("optOutReason", {
              type: "manual",
              message: "Please provide a reason for opting out",
            });
            return;
          }
        }

        // Handle form submission
        await onSubmit(formValues);
      } else {
        console.log("Form validation failed:", form.formState.errors);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            {isEditing
              ? "Update Pension Enrollment"
              : "Pension Enrollment Status"}
          </CardTitle>
          {currentStatus?.isOptedOut ? (
            <Badge variant="destructive" className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Opted Out
            </Badge>
          ) : currentStatus?.currentScheme ? (
            <Badge variant="default" className="bg-green-600">
              Enrolled
            </Badge>
          ) : (
            <Badge variant="secondary">Pending</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {currentStatus?.isOptedOut && (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium">Currently Opted Out</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Opted out on{" "}
                {currentStatus.optOutDate &&
                  format(new Date(currentStatus.optOutDate), "PPP")}
                <br />
                Reason: {currentStatus.optOutReason}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {currentStatus?.currentScheme && !currentStatus.isOptedOut && (
          <Alert className="mb-6">
            <AlertDescription>
              <div className="font-medium">Current Enrollment</div>
              <div className="mt-1 text-sm">
                <div>Scheme: {currentStatus.currentScheme}</div>
                <div>
                  Contributions: {currentStatus.employerContribution}% employer,{" "}
                  {currentStatus.employeeContribution}% employee
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form className="space-y-6">
            <Tabs
              defaultValue={currentStatus?.isOptedOut ? "optOut" : "enroll"}
              onValueChange={(value) => {
                // When switching tabs, update the action but preserve other values where possible
                const currentValues = form.getValues();
                console.log("Switching tabs, current values:", currentValues);

                form.setValue("action", value as "enroll" | "optOut", {
                  shouldValidate: true,
                });

                // Force validation after tab switch
                setTimeout(() => {
                  form.trigger();
                }, 0);
              }}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="enroll">Enroll</TabsTrigger>
                <TabsTrigger value="optOut">Opt Out</TabsTrigger>
              </TabsList>

              <TabsContent value="enroll" className="space-y-6">
                <FormField
                  control={form.control}
                  name="effectiveDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Effective Date</FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? (
                                format(
                                  new Date(field.value + "T00:00:00"),
                                  "dd/MM/yyyy",
                                )
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 pointer-events-auto">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(field.value + "T00:00:00")
                                  : undefined
                              }
                              defaultMonth={
                                field.value
                                  ? new Date(field.value + "T00:00:00")
                                  : undefined
                              }
                              onSelect={(date) => {
                                if (date) {
                                  field.onChange(
                                    date.toISOString().split("T")[0],
                                  );
                                }
                              }}
                              disabled={(date) =>
                                minEffectiveDate
                                  ? date < new Date(minEffectiveDate)
                                  : date < new Date()
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormDescription>
                        {payrunValidation.hasApprovedPayruns
                          ? "Changes can only take effect from the first of the next month due to approved payruns"
                          : "Select when this change should take effect"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="benefitLineId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pension Scheme</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          console.log("Pension scheme selected:", value);

                          // Update minimum contributions based on selected scheme
                          const scheme = availableOptions.find(
                            (opt) => opt.id.toString() === value,
                          );
                          if (scheme) {
                            console.log("Found matching scheme:", scheme);
                            // Only update if the current values are less than minimum
                            if (
                              !employerContribution ||
                              employerContribution <
                                parseFloat(scheme.minimumEmployerContribution)
                            ) {
                              form.setValue(
                                "employerContributionPercent",
                                parseFloat(scheme.minimumEmployerContribution),
                              );
                            }
                            if (
                              !employeeContribution ||
                              employeeContribution <
                                parseFloat(scheme.minimumEmployeeContribution)
                            ) {
                              form.setValue(
                                "employeeContributionPercent",
                                parseFloat(scheme.minimumEmployeeContribution),
                              );
                            }
                          }

                          // Force validation after selection
                          form.trigger("benefitLineId");
                        }}
                        value={
                          field.value ||
                          (activeUniqueOptions.length > 0
                            ? activeUniqueOptions[0].id.toString()
                            : undefined)
                        }
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a pension scheme" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeUniqueOptions.map((option) => (
                            <SelectItem
                              key={option.id}
                              value={option.id.toString()}
                            >
                              {option.displayName} ({option.scheme})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose the pension scheme for enrollment
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Always show contribution fields in enroll tab */}
                <FormField
                  control={form.control}
                  name="employerContributionPercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employer Contribution (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min={
                            selectedOption
                              ? parseFloat(
                                  selectedOption.minimumEmployerContribution,
                                )
                              : 3
                          }
                          value={field.value !== undefined ? field.value : 3}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value =
                              e.target.value === ""
                                ? 3
                                : parseFloat(e.target.value);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {selectedOption
                          ? `Minimum required: ${parseFloat(selectedOption.minimumEmployerContribution)}%`
                          : "Minimum required: 3%"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="employeeContributionPercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employee Contribution (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min={
                            selectedOption
                              ? parseFloat(
                                  selectedOption.minimumEmployeeContribution,
                                )
                              : 5
                          }
                          value={field.value !== undefined ? field.value : 5}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value =
                              e.target.value === ""
                                ? 5
                                : parseFloat(e.target.value);
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {selectedOption
                          ? `Minimum required: ${parseFloat(selectedOption.minimumEmployeeContribution)}%`
                          : "Minimum required: 5%"}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="optOut" className="space-y-6">
                <FormField
                  control={form.control}
                  name="optOutReason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opt-out Reason</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Please provide a reason for opting out of the pension scheme"
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="button"
              className="w-full"
              disabled={isSubmitting}
              data-testid="submit-pension-enrollment"
              onClick={() => {
                // First ensure the benefitLineId is set if we're enrolling
                const currentAction = form.getValues("action");
                if (currentAction === "enroll") {
                  const currentBenefitLineId = form.getValues("benefitLineId");
                  if (!currentBenefitLineId && activeUniqueOptions.length > 0) {
                    // Set a default if none is selected
                    form.setValue(
                      "benefitLineId",
                      activeUniqueOptions[0].id.toString(),
                    );
                  }
                }

                // Then trigger our submit handler
                handleFormSubmit();
              }}
            >
              {isSubmitting
                ? "Submitting..."
                : isEditing
                  ? "Update Enrollment"
                  : "Submit Enrollment"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default PensionSelectionForm;
